import React, {
    FormEvent,
    MouseEvent,
    useEffect,
    useRef,
    useState,
    KeyboardEvent,
    lazy,
    Suspense,
} from 'react';
import Chevron from '../../../../../support/components/Chevron/Chevron';
import duration from '../../../../../assets/ford-rides/duration.svg';
import distance from '../../../../../assets/ford-rides/distance.svg';
import gaugeIcon from '../../../../../assets/ford-rides/gauge-icon.svg';
import { ReducerInitialState } from '../../../../../models/ford-rides-trips-service-model';
import {
    ReducerInitialStates,
    ShiftRides,
} from '../../../../../contexts/ford-rides-context/ford-rides-context';
import useFordRidesContext from '../../hooks/use-ford-rides-context';
import moment from 'moment-timezone';
import { PrimaryButton } from '../../../../common';
import { FordRidesContentProps } from '../../hooks/use-ford-rides-content';
import './ford-rides-accordion.scss';
import { ActivityIndicator } from '../../../../common/activity-indicator/activity-indicator';

const BusinessDetails = lazy(() =>
    import('../../business-details/business-details')
);

interface Props extends ReducerInitialState {
    accordionCategory: keyof ReducerInitialStates;
    setTripId?: (data: string) => void;
    tripId?: string;
    fordRidesData: FordRidesContentProps;
}

interface ConfirmAccordionInfoProps {
    infoData: React.JSX.Element;
    fordRidesData: FordRidesContentProps;
}

export const ConfirmAccordionInfo = (props: ConfirmAccordionInfoProps) => {
    const { resolve, reject } = useFordRidesContext();
    return (
        <div className="confirm-accordion-options">
            {props.infoData}
            <div className="modal-info-buttons">
                <PrimaryButton
                    color="dark"
                    fill="fill"
                    chevron={false}
                    onClick={resolve}
                >
                    {props.fordRidesData.confirmModalConfirmCta}
                </PrimaryButton>
                <PrimaryButton
                    color="dark"
                    fill="outline"
                    chevron={false}
                    onClick={reject}
                >
                    {props.fordRidesData.confirmModalCancelCta}
                </PrimaryButton>
            </div>
        </div>
    );
};

function FordRidesAccordion(props: Props) {
    const { setTripId, tripId, fordRidesData, ...validProps } = props;

    const { isConfirmed, shiftRidesData, state } = useFordRidesContext();

    const [expand, setExpand] = useState(false);
    const [selectValue, setSelectValue] = useState(
        props.tripType?.toLowerCase() || ''
    );

    const tripTypeValue = useRef(props.tripType?.toLowerCase() ?? 'unassigned');

    const handleChange = async (e: FormEvent<HTMLSelectElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const target: HTMLSelectElement = e.target as HTMLSelectElement;
        const { value } = target;
        if (value === selectValue) {
            return;
        }

        const infoData = (
            <div className="confirm-accordion-info">
                <p className="confirm-message">
                    {fordRidesData.confirmModalText}{' '}
                </p>
                <div className="confirm-message-data">
                    <span className="duration info-item">
                        <img src={duration} alt="duration" />{' '}
                        <span>{props.tripDuration}</span>
                    </span>
                    <span className="distance info-item">
                        <img src={distance} alt="distance" />{' '}
                        <span>{props.tripDistance}</span>
                    </span>
                    <span className="distance info-item">
                        <img src={gaugeIcon} alt="odometer reading" />{' '}
                        <span>{props.tripStartOdoRead}</span>
                    </span>
                </div>
                <div>
                    {fordRidesData.asText}{' '}
                    {fordRidesData[value as keyof FordRidesContentProps]}
                </div>
            </div>
        );

        const response = await isConfirmed(
            <ConfirmAccordionInfo
                infoData={infoData}
                fordRidesData={fordRidesData}
            />
        );

        if (!response) {
            return;
        }

        const data: ShiftRides = {
            from: (target.dataset.accordioncategory?.toLowerCase() ??
                props.accordionCategory.toLowerCase()) as keyof ReducerInitialStates,
            to: value as keyof ReducerInitialStates,
            id: target.id,
        };
        if (data.from === data.to) {
            setSelectValue(value);
            setExpand(false);
        } else if (
            ['unassigned', 'personal'].includes(data.from) &&
            ['business'].includes(data.to)
        ) {
            setSelectValue(value);
            setTripId && setTripId(props?.tripRowId ?? '');
            setExpand(true);
        } else if (
            ['business', 'unassigned'].includes(data.from) &&
            ['personal'].includes(data.to)
        ) {
            setSelectValue(value);
            setExpand(false);
            shiftRidesData(data).catch((e: any) => {
                setSelectValue(tripTypeValue.current);
                console.error(e);
            });
        }
    };

    useEffect(() => {
        const result = tripId === props.tripRowId;
        setExpand(result);
    }, [tripId, props.tripRowId]);

    const handleAccordionClick = (
        e: MouseEvent<HTMLDivElement & HTMLButtonElement> &
            KeyboardEvent<HTMLDivElement & HTMLButtonElement>
    ) => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.target as HTMLElement;
        if (
            target.tagName.toLowerCase() === 'button' ||
            target.tagName.toLowerCase() === 'div'
        ) {
            const accordionState = () => {
                setExpand(prev => {
                    return !prev;
                });
                setSelectValue(prevSelect => {
                    if (prevSelect === tripTypeValue.current) {
                        return prevSelect;
                    }
                    return tripTypeValue.current;
                });
                setTripId && setTripId(props?.tripRowId ?? '');
            };
            if (
                e.type === 'click' ||
                (e.type === 'keyup' &&
                    e.key.toLowerCase() === 'enter' &&
                    target.tagName.toLowerCase() !== 'button')
            ) {
                accordionState();
            }
        }
    };

    return (
        <div className="ford-rides__list-panel-wrapper">
            <div
                className="ford-rides__list-panel"
                role="button"
                {...(props.accordionCategory.match(/business/gi) ||
                selectValue === 'business'
                    ? {
                          onClick: handleAccordionClick,
                          onKeyUp: handleAccordionClick,
                      }
                    : {})}
                tabIndex={0}
                aria-expanded={expand}
                aria-label="Accordion Panel"
            >
                <div className="rides-summary">
                    <div className="day-date__info">
                        <span className="day-date">
                            {moment
                                .utc(props.tripStartTime)
                                .tz(moment.tz.guess())
                                .format('ddd, MMM DD YYYY HH:mm:ss A z')}
                        </span>
                        {(props.accordionCategory.match(/business/gi) ||
                            selectValue === 'business') && (
                            <button className="toggle-info">
                                <span>{fordRidesData.toggleAccordionText}</span>
                                <Chevron direction={expand ? 'up' : 'down'} />
                            </button>
                        )}
                    </div>
                    <div className="ride-info">
                        <span className="duration tooltip info-item">
                            <img src={duration} alt="" />
                            <span>{props.tripDuration}</span>
                            <span className="tooltiptext">
                                {fordRidesData.ridesDurationText}
                            </span>
                        </span>
                        <span className="distance tooltip info-item">
                            <img src={distance} alt="" />
                            <span>{props.tripDistance}</span>
                            <span className="tooltiptext">
                                {fordRidesData.ridesDistanceText}
                            </span>
                        </span>
                        <span className="distance tooltip info-item">
                            <img src={gaugeIcon} alt="" />
                            <span>{props.tripStartOdoRead}</span>
                            <span className="tooltiptext">
                                {fordRidesData.ridesOdoStartText}
                            </span>
                        </span>
                        <select
                            id={props.tripRowId}
                            className={`classification ${selectValue ||
                                'unassigned'}`}
                            onChange={handleChange}
                            value={selectValue}
                            data-accordioncategory={props.accordionCategory.toLowerCase()}
                            name="trip-selector"
                            aria-label="Trip Selector"
                        >
                            {Object.keys(state)
                                .filter(data => {
                                    if (
                                        ['business', 'personal'].includes(
                                            props.accordionCategory.toLowerCase()
                                        )
                                    ) {
                                        if (data === 'unassigned') {
                                            return false;
                                        } else {
                                            return true;
                                        }
                                    } else {
                                        return true;
                                    }
                                })
                                .map(data => {
                                    return (
                                        <option
                                            value={data}
                                            key={`${data}-${props.tripRowId}`}
                                        >
                                            {
                                                fordRidesData[
                                                    data as keyof FordRidesContentProps
                                                ]
                                            }
                                        </option>
                                    );
                                })}
                        </select>
                    </div>
                </div>
            </div>
            {expand && selectValue.toLowerCase() === 'business' ? (
                <Suspense fallback={<ActivityIndicator />}>
                    <BusinessDetails {...validProps} />
                </Suspense>
            ) : (
                <></>
            )}
        </div>
    );
}

export default FordRidesAccordion;
